/**
 * Controller input, wired to the app: one router fed the store's bindings, and the MIDI backend once the user enables it.
 *
 * `startInput` is cheap and never prompts: it indexes bindings, and starts MIDI only when the browser already grants it (`resumeMidi`). Otherwise MIDI waits for `enableMidi` from a user action (see `midi/access.js`).
 */
import { IS_DEV } from '~/lib/constants'
import store from '~/store'
import { setMidiInputs, setMidiOn, setMidiStatus } from '~/store/input'
import { createFeedback, FEEDBACK_MS } from './feedback'
import { midiSupported } from './midi/access'
import { feedbackMessages } from './midi/feedback'
import { createMidiBackend } from './midi'
import { createRouter } from './router'
import { readTarget, resolvePort } from './writers'

let router = null
let midi = null
let feedback = null
let feedbackTimer
let starting = null
let stopSync = null

/**
 * ⚠️ A grant outlives the page, and so does `midiOn` — so a show reloaded mid-set must not go deaf until someone turns MIDI on again. When the permission is already granted, `requestMIDIAccess` resolves without a prompt, so it needs no user action. `permissions.query` throws where `midi` is not a known permission (Firefox), which reads as not granted.
 */
const resumeMidi = async () => {
	if (!midiSupported() || !store.getState().input.midiOn) return
	try {
		const p = await navigator.permissions?.query({ name: 'midi' })
		if (p?.state === 'granted' && router) enableMidi()
	} catch {
		// Unknown permission name: Learn asks instead.
	}
}

/** Start routing; returns the stop. */
export const startInput = () => {
	if (router) return stopInput
	router = createRouter({ resolvePort })
	let bindings = null
	let disabledInputs = null
	let preset
	const sync = () => {
		const state = store.getState()
		// A preset load moves a whole page of params: show them all again.
		if (state.playback?.current_preset_index !== preset) {
			preset = state.playback?.current_preset_index
			feedback?.invalidate()
		}
		const s = state.input
		if (s.disabledInputs !== disabledInputs) {
			disabledInputs = s.disabledInputs
			midi?.configure(s)
		}
		if (s.bindings === bindings) return
		bindings = s.bindings
		router.setBindings(bindings)
		midi?.configure(s)
		feedback?.setBindings(bindings)
	}
	sync()
	const unsubscribe = store.subscribe(sync)
	// A gesture still open when the page goes would never settle: its held frames would be lost.
	const release = () => router?.release()
	window.addEventListener('pagehide', release)
	stopSync = () => {
		unsubscribe()
		window.removeEventListener('pagehide', release)
	}
	if (IS_DEV)
		window.input = {
			router,
			enableMidi,
			disableMidi,
			get feedback() {
				return feedback
			},
		}
	resumeMidi()
	return stopInput
}

export const stopInput = () => {
	disableMidi()
	stopSync?.()
	stopSync = null
	router?.release()
	router = null
	if (IS_DEV) window.input = null
}

/**
 * Turn MIDI on. ⚠️ From a user action only, unless the permission is already granted (`resumeMidi`).
 *
 * One start at a time: a second call while the prompt is up (Learn entered, left and entered again) joins the first rather than building a second backend over the same ports.
 *
 * @returns whether MIDI is now running
 */
export const enableMidi = () => {
	starting ??= startMidi().finally(() => {
		starting = null
	})
	return starting
}

const startMidi = async () => {
	startInput()
	if (midi) return true
	if (!midiSupported()) {
		store.dispatch(setMidiStatus('unsupported'))
		return false
	}
	store.dispatch(setMidiStatus('pending'))
	const backend = createMidiBackend({
		router,
		onPorts: ports => {
			// A controller plugged in has whatever its LEDs and faders last showed.
			feedback?.invalidate()
			store.dispatch(setMidiInputs(ports))
		},
		// Its first message is when its feedback starts: show it everything.
		onPlayed: () => feedback?.invalidate(),
	})
	backend.configure(store.getState().input)
	try {
		await backend.start()
	} catch (e) {
		store.dispatch(setMidiStatus(e?.name === 'NotSupportedError' ? 'unsupported' : 'denied'))
		store.dispatch(setMidiOn(false))
		return false
	}
	// Stopped while the prompt was up.
	if (!router) {
		backend.stop()
		return false
	}
	midi = backend
	feedback = createFeedback({
		readTarget,
		busy: id => router?.busy(id) ?? true,
		encode: (b, pos) => (b.source?.backend === 'midi' ? feedbackMessages(b, pos) : null),
		send: (_b, msgs) => midi?.send(msgs) ?? false,
	})
	feedback.setBindings(store.getState().input.bindings)
	feedbackTimer = setInterval(feedback.tick, FEEDBACK_MS)
	store.dispatch(setMidiStatus('ready'))
	store.dispatch(setMidiOn(true))
	return true
}

/** The user's MIDI switch: on asks for access (⚠️ from a user action), off stops it and stays off across reloads. */
export const setMidiPower = on => {
	if (on) return enableMidi()
	disableMidi()
	store.dispatch(setMidiOn(false))
}

export const disableMidi = () => {
	if (!midi) return
	clearInterval(feedbackTimer)
	feedback = null
	midi.stop()
	midi = null
	router?.release()
	store.dispatch(setMidiInputs([]))
	store.dispatch(setMidiStatus('off'))
}

/** Controls waiting for pickup: target → hardware position, see `router.ghosts`. */
export const inputGhosts = () => router?.ghosts() ?? new Map()

/** Every decoded MIDI message, before routing; a no-op unsubscribe while MIDI is off. */
export const tapMidi = fn => midi?.tap(fn) ?? (() => {})
